/**
 * Tipos do domínio de Exportação (M9) no frontend, alinhados ao contrato
 * congelado (respostas no nível superior; lista em {data, nextCursor}).
 * Nenhum dado pessoal trafega pela UI além do necessário (RN-M11-001).
 */

export type ExportacaoFormato = 'csv' | 'jsonl';
/** `escolas` (ADR-041): uma linha por escola com visita associada, pedida na tela de Escolas. */
export type ExportacaoEscopo = 'tudo' | 'filtro' | 'escola' | 'escolas';
/** Status do contrato (GET /exportacoes/{id}); `expirada` = pronta com o link vencido. */
export const EXPORTACAO_STATUS = ['na_fila', 'gerando', 'pronta', 'expirada', 'falha'] as const;
export type ExportacaoStatus = (typeof EXPORTACAO_STATUS)[number];
/** Status que não mudam mais — o polling da lista para quando só há estes. */
export const STATUS_TERMINAIS: ReadonlySet<ExportacaoStatus> = new Set(['pronta', 'expirada', 'falha']);

/** Filtro de exportação (mesmos campos do M7; todos opcionais). */
export interface ExportacaoFiltro {
  uf?: string;
  municipioIbge?: string;
  escolaInep?: string;
  de?: string;
  ate?: string;
  resultado?: string;
  tipoInstalacao?: string;
  contratada?: string;
  tecnico?: string;
  imei?: string;
  iccid?: string;
  cnpj?: string;
  marcadores?: 'OK' | 'CORRIGIDO' | 'ALERTA' | 'ERRO';
  tipoDocumento?: string;
  pasta?: string;
  q?: string;
  /** ADR-038: só com imagem ou só da planilha (sem imagem). */
  evidencia?: 'imagem' | 'planilha';
  /** ADR-038: inclui as linhas de referência (`"1"`). */
  incluirReferencias?: '1';
  /** Spec ajustes-telas-amostra: só as visitas com outras evidências ligadas. */
  outrasEvidencias?: '1';
}

/** ADR-041: o filtro da exportação por escola — os filtros da tela de Escolas (`FiltroEscolasSchema` do backend). */
export interface EscolaExportacaoFiltro {
  uf?: string;
  municipio?: string;
  q?: string;
  suspeitas?: boolean;
  evidencia?: 'imagem' | 'planilha';
}

/** O filtro guardado no pedido: o das visitas ou, no escopo `escolas`, o das escolas. */
export type FiltroExportacaoGuardado = ExportacaoFiltro | EscolaExportacaoFiltro;

/** Body do POST /exportacoes. */
export interface CriarExportacaoInput {
  readonly formato: ExportacaoFormato;
  readonly escopo?: ExportacaoEscopo;
  readonly filtro?: FiltroExportacaoGuardado;
  readonly incluirLidos?: boolean;
}

/** Resposta do POST /exportacoes (nível superior). */
export interface CriarExportacaoResponse {
  readonly exportacaoId: string;
  readonly status: 'na_fila';
  readonly formato: ExportacaoFormato;
}

/** Detalhe GET /exportacoes/{id} (nível superior). */
export interface ExportacaoDetalhe {
  readonly exportacaoId: string;
  readonly status: ExportacaoStatus;
  readonly formato: ExportacaoFormato;
  readonly filtro: FiltroExportacaoGuardado;
  readonly linhas: number | null;
  readonly tamanhoBytes: number | null;
  readonly expiraEm: string | null;
}

/** Item da lista GET /exportacoes (dentro de `data`). */
export interface ExportacaoListItem {
  readonly exportacaoId: string;
  readonly status: ExportacaoStatus;
  readonly formato: ExportacaoFormato;
  readonly escopo: ExportacaoEscopo;
  readonly filtro: FiltroExportacaoGuardado;
  readonly linhas: number | null;
  readonly tamanhoBytes: number | null;
  readonly solicitadaEm: string;
  readonly expiraEm: string | null;
}

/** Resposta GET /exportacoes/{id}/download (nível superior). */
export interface DownloadExportacao {
  readonly downloadUrl: string;
  readonly expiresIn: number;
}

/** Campos de filtro que o `POST /exportacoes` aceita (schema `.strict()` do backend). */
const CHAVES_FILTRO: ReadonlyArray<keyof ExportacaoFiltro> = [
  'uf', 'municipioIbge', 'escolaInep', 'de', 'ate', 'resultado', 'tipoInstalacao', 'contratada',
  'tecnico', 'imei', 'iccid', 'cnpj', 'marcadores', 'tipoDocumento', 'pasta', 'q', 'evidencia',
];

/**
 * Converte os filtros da tela (Visitas, M7) no filtro da exportação (M9): só as
 * chaves que o backend aceita — sem paginação (`cursor`/`limit`), que o schema
 * estrito recusaria com 400 — e sem campos vazios.
 */
export function filtroParaExportacao(filtros: Readonly<Record<string, unknown>>): ExportacaoFiltro {
  const saida: Record<string, unknown> = {};
  for (const chave of CHAVES_FILTRO) {
    const valor = filtros[chave];
    if (typeof valor === 'string' && valor.trim() !== '') {
      saida[chave] = valor;
    }
  }
  // ADR-038: a caixa "Incluir referências" chega como booleano (tela) ou "1" (query).
  if (filtros.incluirReferencias === true || filtros.incluirReferencias === '1') {
    saida.incluirReferencias = '1';
  }
  // Spec ajustes-telas-amostra: a caixa "Com outras evidências" (booleano na tela, "1" na query).
  if (filtros.outrasEvidencias === true || filtros.outrasEvidencias === '1') {
    saida.outrasEvidencias = '1';
  }
  return saida as ExportacaoFiltro;
}


/**
 * ADR-041: os filtros da tela de Escolas no filtro da exportação por escola — só as chaves do `FiltroEscolasSchema`
 * (sem o grupo, o `comVisitas` e a paginação, que o esquema estrito recusaria), sem vazios; `suspeitas` só quando ligado.
 */
export function filtroEscolasParaExportacao(tela: object): EscolaExportacaoFiltro {
  const filtros = tela as Readonly<Record<string, unknown>>;
  const saida: EscolaExportacaoFiltro = {};
  for (const chave of ['uf', 'municipio', 'q'] as const) {
    const valor = filtros[chave];
    if (typeof valor === 'string' && valor.trim() !== '') {
      saida[chave] = valor;
    }
  }
  if (filtros.suspeitas === true) {
    saida.suspeitas = true;
  }
  if (filtros.evidencia === 'imagem' || filtros.evidencia === 'planilha') {
    saida.evidencia = filtros.evidencia;
  }
  return saida;
}
